/**
 * Motor de combate. TypeScript puro, sin React, sin acceso a red, sin Date.now().
 *
 * Es la pieza que en la Fase 2 se ejecutará también en el servidor para validar
 * partidas, así que se mantiene deliberadamente determinista: mismas entradas
 * (semilla + secuencia de jugadas) => mismo resultado, byte a byte.
 *
 * Frontera pública: todas las funciones exportadas reciben un estado y devuelven
 * uno nuevo. Nunca modifican el que se les pasa. Por dentro clonan una vez y
 * mutan el clon, que es más legible que encadenar spreads de tres niveles.
 */

import { LOCATION_POOL, getCardMaster } from '@/data';
import type {
  ActionResult,
  BoardCard,
  CardInstance,
  GameState,
  MatchResult,
  PlayerSide,
  PlayerState,
  RevealStep,
  TurnResolution,
  Zone,
  ZoneIndex,
} from '@/types/game';
import { MAX_TURNS, RULES, STARTING_HAND_SIZE } from '@/types/game';
import {
  applyOnRevealEffect,
  computeTotalPower,
  computeZonePower,
  isZoneLockedOnTurn,
  occupancyOf,
  zoneCapacity,
} from './effects';
import { nextInt, randomSeed, shuffle } from './rng';
import {
  cloneState,
  cardsOf,
  commitRng,
  drawCards,
  findBoardCard,
  makeCardInstance,
  opponentOf,
  pushLog,
  rngOf,
} from './state';

/* -------------------------------------------------------------------------- */
/* Creación de partida                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Una carta del mazo tal y como entra en la partida.
 *
 * Se admite el id suelto (nivel 1, que es el caso del mazo de pruebas) o el par
 * carta+nivel. El nivel hace falta para el PvP: enfrentarse a un mazo con cartas
 * fusionadas y jugarlas como si fueran de nivel 1 le quitaría al rival justo lo
 * que le costó conseguir.
 */
export type DeckEntry = string | { masterId: string; level: number };

export interface CreateMatchOptions {
  playerName?: string;
  opponentName?: string;
  /** DECK_SIZE cartas. Por defecto, el mazo inicial. */
  playerDeckIds: readonly DeckEntry[];
  opponentDeckIds: readonly DeckEntry[];
  /** Fijar la semilla hace la partida reproducible (tests, repeticiones). */
  seed?: number;
}

function buildDeck(deck: readonly DeckEntry[], owner: PlayerSide): CardInstance[] {
  return deck.map((entry, index) =>
    typeof entry === 'string'
      ? makeCardInstance(entry, owner, 1, index)
      : makeCardInstance(entry.masterId, owner, entry.level, index),
  );
}

function emptyPlayer(side: PlayerSide, name: string, deck: CardInstance[]): PlayerState {
  return {
    side,
    name,
    avatarUrl: null,
    energy: 0,
    maxEnergy: 0,
    bonusEnergyNextTurn: 0,
    deck,
    hand: [],
    isReady: false,
  };
}

/**
 * Reparte una partida nueva y la deja lista en el turno 1, fase de planificación.
 *
 * Las 3 ubicaciones se sortean del pool sin repetición. Con un pool mayor que 3
 * cada partida tiene un tablero distinto, que es lo que hace que el mismo mazo
 * se juegue diferente cada vez.
 */
export function createMatch(options: CreateMatchOptions): GameState {
  const seed = options.seed ?? randomSeed();
  let rng = { seed, cursor: 0 };

  const [shuffledLocations, afterLocations] = shuffle(rng, LOCATION_POOL);
  rng = afterLocations;

  const zones = ([0, 1, 2] as ZoneIndex[]).map((index): Zone => {
    const location = shuffledLocations[index];
    if (!location) throw new Error('El pool de ubicaciones necesita al menos 3 entradas');
    return { index, location, cards: [] };
  }) as [Zone, Zone, Zone];

  const [playerDeck, afterPlayerShuffle] = shuffle(rng, buildDeck(options.playerDeckIds, 'PLAYER'));
  rng = afterPlayerShuffle;
  const [opponentDeck, afterOpponentShuffle] = shuffle(
    rng,
    buildDeck(options.opponentDeckIds, 'OPPONENT'),
  );
  rng = afterOpponentShuffle;

  // Quién revela primero en el turno 1 es puro azar: con el tablero vacío nadie
  // "va ganando" todavía.
  const [firstPriority, afterPriority] = nextInt(rng, 2);
  rng = afterPriority;

  const state: GameState = {
    matchId: `match-${seed}`,
    seed,
    rngCursor: rng.cursor,
    turn: 0,
    maxTurns: MAX_TURNS,
    phase: 'PLANNING',
    zones,
    players: {
      PLAYER: emptyPlayer('PLAYER', options.playerName ?? 'Tú', playerDeck),
      OPPONENT: emptyPlayer('OPPONENT', options.opponentName ?? 'Rival', opponentDeck),
    },
    priority: firstPriority === 0 ? 'PLAYER' : 'OPPONENT',
    playCounter: 0,
    result: null,
    log: [],
  };

  drawCards(state, 'PLAYER', STARTING_HAND_SIZE);
  drawCards(state, 'OPPONENT', STARTING_HAND_SIZE);
  beginTurn(state);
  return state;
}

/**
 * Abre el turno siguiente: sube el contador, recarga energía y reparte el robo.
 * Muta el estado (uso interno).
 *
 * La energía es igual al número de turno, con un techo. El techo importa en las
 * partidas largas: la carta más cara cuesta 6, así que a partir del turno 7 la
 * energía dejaría de ser una decisión y pasaría a sobrar. Medido, sin techo se
 * desperdiciaba el 44 % de la energía en una partida de 10 turnos.
 *
 * Lo que sobra al acabar el turno se pierde; solo se arrastra la bonificación
 * puntual de cartas tipo Alquimista.
 */
function beginTurn(state: GameState): void {
  state.turn += 1;
  state.phase = 'PLANNING';

  for (const side of ['PLAYER', 'OPPONENT'] as const) {
    const player = state.players[side];
    player.energy = Math.min(state.turn, RULES.maxEnergyPerTurn) + player.bonusEnergyNextTurn;
    player.maxEnergy = player.energy;
    player.bonusEnergyNextTurn = 0;
    player.isReady = false;
    drawCards(state, side, 1);
  }
}

/* -------------------------------------------------------------------------- */
/* Acciones de la fase de planificación                                       */
/* -------------------------------------------------------------------------- */

/**
 * Coloca una carta de la mano en una zona, boca abajo hasta la revelación.
 *
 * Devuelve un error legible en vez de lanzar: cada uno de estos casos es una
 * jugada que el jugador puede intentar, no un fallo de programación. La UI los
 * enseña como aviso.
 */
export function playCard(
  state: GameState,
  side: PlayerSide,
  instanceId: string,
  zoneIndex: ZoneIndex,
): ActionResult {
  if (state.phase !== 'PLANNING') {
    return { ok: false, error: 'No puedes jugar cartas durante la revelación.' };
  }

  const next = cloneState(state);
  const player = next.players[side];
  const handIndex = player.hand.findIndex((card) => card.instanceId === instanceId);
  if (handIndex === -1) return { ok: false, error: 'Esa carta no está en tu mano.' };

  const card = player.hand[handIndex];
  if (!card) return { ok: false, error: 'Esa carta no está en tu mano.' };

  const master = getCardMaster(card.masterId);
  if (master.cost > player.energy) {
    return { ok: false, error: `Te faltan ${master.cost - player.energy} de energía.` };
  }

  const zone = next.zones[zoneIndex];
  if (isZoneLockedOnTurn(zone, next.turn)) {
    return { ok: false, error: `${zone.location.name} está bloqueada este turno.` };
  }
  if (occupancyOf(zone, side) >= zoneCapacity(zone)) {
    return { ok: false, error: `${zone.location.name} ya está llena (${zoneCapacity(zone)}).` };
  }

  player.hand.splice(handIndex, 1);
  player.energy -= master.cost;

  const boardCard: BoardCard = {
    ...card,
    zone: zoneIndex,
    revealed: false,
    playedOnTurn: next.turn,
    playOrder: next.playCounter,
    modifiers: [],
  };
  next.playCounter += 1;
  zone.cards.push(boardCard);

  return { ok: true, state: next };
}

/**
 * Devuelve a la mano una carta colocada este mismo turno y reembolsa su energía.
 *
 * Sin esto, un toque mal dado en el móvil arruina el turno. Solo funciona antes
 * de revelar y solo con cartas de este turno: una carta ya revelada es
 * definitiva.
 */
export function recallCard(state: GameState, side: PlayerSide, instanceId: string): ActionResult {
  if (state.phase !== 'PLANNING') {
    return { ok: false, error: 'No puedes retirar cartas durante la revelación.' };
  }

  const next = cloneState(state);
  const card = findBoardCard(next, instanceId);
  if (!card || card.owner !== side) return { ok: false, error: 'Esa carta no es tuya.' };
  if (card.revealed || card.playedOnTurn !== next.turn) {
    return { ok: false, error: 'Solo puedes retirar cartas colocadas este turno.' };
  }

  const zone = next.zones[card.zone];
  zone.cards = zone.cards.filter((other) => other.instanceId !== instanceId);

  const player = next.players[side];
  player.energy += getCardMaster(card.masterId).cost;
  player.hand.push({
    instanceId: card.instanceId,
    masterId: card.masterId,
    level: card.level,
    owner: card.owner,
  });

  return { ok: true, state: next };
}

/**
 * Por qué esa carta no se puede jugar ahora mismo, o null si sí se puede.
 *
 * Existe porque atenuar la carta y callarse no basta: el jugador ve una carta
 * apagada y no sabe si le falta energía, si las zonas están llenas o si el juego
 * se ha colgado. Y en un juego de cartas, "no entiendo por qué no puedo" es la
 * forma más rápida de que alguien lo cierre.
 *
 * Vive en el motor y no en la interfaz porque es conocimiento de reglas. Si se
 * escribiera en el componente, cambiar una regla dejaría el mensaje mintiendo.
 */
export function whyCannotPlay(
  state: GameState,
  side: PlayerSide,
  instanceId: string,
): string | null {
  if (state.phase !== 'PLANNING') return 'Espera a que termine la revelación.';

  const player = state.players[side];
  const card = player.hand.find((item) => item.instanceId === instanceId);
  if (!card) return 'Esa carta ya no está en tu mano.';

  const master = getCardMaster(card.masterId);
  if (master.cost > player.energy) {
    return `${master.name} cuesta ${master.cost} y te quedan ${player.energy} de energía.`;
  }

  const open = state.zones.filter(
    (zone) => !isZoneLockedOnTurn(zone, state.turn) && occupancyOf(zone, side) < zoneCapacity(zone),
  );
  if (open.length === 0) return 'No te queda hueco libre en ninguna zona.';

  return null;
}

/* -------------------------------------------------------------------------- */
/* Fase de revelación                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Quién revela primero.
 *
 * La iniciativa es del que va ganando en poder total sobre el tablero. En caso
 * de empate exacto se decide al azar, pero con el RNG del estado: dos ejecuciones
 * de la misma partida toman la misma decisión.
 */
function resolvePriority(state: GameState): PlayerSide {
  const playerPower = computeTotalPower(state, 'PLAYER');
  const opponentPower = computeTotalPower(state, 'OPPONENT');
  if (playerPower !== opponentPower) return playerPower > opponentPower ? 'PLAYER' : 'OPPONENT';

  const [coin, next] = nextInt(rngOf(state), 2);
  commitRng(state, next);
  return coin === 0 ? 'PLAYER' : 'OPPONENT';
}

/**
 * Cierra el turno: revela todo lo colocado, ejecuta los ON_REVEAL y avanza.
 *
 * Devuelve la secuencia de pasos además del estado final. Cada paso lleva su
 * propia instantánea, así que la UI reproduce la revelación carta a carta sin
 * tener que recalcular nada — y lo que se anima es exactamente lo que el motor
 * decidió.
 *
 * El bando con iniciativa revela todas sus cartas antes que el rival: eso es lo
 * que hace que ir ganando sea un arma de doble filo, porque el rival ve el
 * resultado y coloca sus efectos encima.
 */
export function resolveTurn(state: GameState): TurnResolution {
  if (state.phase !== 'PLANNING') {
    return { steps: [], finalState: state };
  }

  const next = cloneState(state);
  next.phase = 'REVEALING';
  next.priority = resolvePriority(next);

  const order: PlayerSide[] = [next.priority, opponentOf(next.priority)];
  const steps: RevealStep[] = [];

  for (const side of order) {
    const pending = next.zones
      .flatMap((zone) => cardsOf(zone, side))
      .filter((card) => !card.revealed)
      .sort((a, b) => a.playOrder - b.playOrder);

    for (const pendingCard of pending) {
      // Se vuelve a buscar la carta porque las instantáneas anteriores clonaron
      // el estado: `pendingCard` puede apuntar a un objeto ya sustituido.
      const card = findBoardCard(next, pendingCard.instanceId);
      if (!card) continue;

      card.revealed = true;
      const master = getCardMaster(card.masterId);
      const effectMessage = applyOnRevealEffect(next, card);
      const message = effectMessage ?? `${master.name} se despliega`;
      pushLog(next, message);

      steps.push({
        boardCardId: card.instanceId,
        side,
        zone: card.zone,
        cardName: master.name,
        message,
        stateAfter: cloneState(next),
      });
    }
  }

  if (next.turn >= next.maxTurns) {
    next.phase = 'FINISHED';
    next.result = evaluateMatch(next);
    pushLog(next, next.result.reason);
  } else {
    beginTurn(next);
  }

  return { steps, finalState: next };
}

/* -------------------------------------------------------------------------- */
/* Resultado                                                                  */
/* -------------------------------------------------------------------------- */

export interface ZoneScore {
  zone: ZoneIndex;
  playerPower: number;
  opponentPower: number;
  /** null = zona empatada, que no la controla nadie. */
  leader: PlayerSide | null;
}

/** Marcador de las tres zonas. Lo usa tanto la UI como el cálculo del ganador. */
export function getZoneScores(state: GameState): ZoneScore[] {
  return state.zones.map((zone) => {
    const playerPower = computeZonePower(zone, 'PLAYER');
    const opponentPower = computeZonePower(zone, 'OPPONENT');
    return {
      zone: zone.index,
      playerPower,
      opponentPower,
      leader:
        playerPower === opponentPower ? null : playerPower > opponentPower ? 'PLAYER' : 'OPPONENT',
    };
  });
}

/**
 * Decide la partida.
 *
 * 1. Gana quien controle más zonas (con 3 zonas, eso es tener 2 o las 3).
 * 2. Si empatan a zonas, gana quien tenga más poder total sumando las tres.
 * 3. Si también empatan en poder, es tablas.
 *
 * La regla 1 se expresa como "más zonas" y no como "al menos 2" para cubrir
 * también el caso 1-0 con dos zonas empatadas, que el enunciado no contempla y
 * que sería raro resolver por poder total.
 */
export function evaluateMatch(state: GameState): MatchResult {
  const scores = getZoneScores(state);
  const zonesWon: Record<PlayerSide, number> = { PLAYER: 0, OPPONENT: 0 };
  for (const score of scores) {
    if (score.leader) zonesWon[score.leader] += 1;
  }

  const totalPower: Record<PlayerSide, number> = {
    PLAYER: computeTotalPower(state, 'PLAYER'),
    OPPONENT: computeTotalPower(state, 'OPPONENT'),
  };

  if (zonesWon.PLAYER !== zonesWon.OPPONENT) {
    const winner: PlayerSide = zonesWon.PLAYER > zonesWon.OPPONENT ? 'PLAYER' : 'OPPONENT';
    return {
      winner,
      zonesWon,
      totalPower,
      reason: `${state.players[winner].name} controla ${zonesWon[winner]} de 3 zonas.`,
    };
  }

  if (totalPower.PLAYER !== totalPower.OPPONENT) {
    const winner: PlayerSide = totalPower.PLAYER > totalPower.OPPONENT ? 'PLAYER' : 'OPPONENT';
    return {
      winner,
      zonesWon,
      totalPower,
      reason: `Empate a zonas: decide el poder total (${totalPower[winner]} contra ${totalPower[opponentOf(winner)]}).`,
    };
  }

  return {
    winner: 'DRAW',
    zonesWon,
    totalPower,
    reason: 'Empate absoluto: mismas zonas y mismo poder total.',
  };
}

/* -------------------------------------------------------------------------- */
/* Reexportaciones cómodas para la UI                                         */
/* -------------------------------------------------------------------------- */

export {
  basePowerOf,
  computeCardPower,
  computeTotalPower,
  computeZonePower,
  isZoneLockedOnTurn,
  locationDeltaFor,
  occupancyOf,
  zoneCapacity,
} from './effects';
